import PageLoader from '@components/PageLoader'
import { useAdminExpenses } from '@hooks/useAdminExpenses'
import { useAdminPayups } from '@hooks/useAdminPayups'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AdminHeading } from '../AdminLayout.styles'
import { useAdminFilter } from '../AdminFilterContext'
import ListStats from '../ListStats'
import { formatAmount, formatDate } from '../adminFormat'
import {
	DeletedTag,
	EmptyState as ListEmptyState,
	PageButton,
	PageStatus,
	Pagination,
	ResultCount,
	Row,
	SearchInput,
	Table,
	TableWrap,
	Td,
	Th,
	Toolbar,
} from '../adminList.styles'
import { ErrorState, SectionHeading, StaticRow } from './ExpensesPage.styles'

const PAGE_SIZE = 25

/**
 * Admin Expenses: a searchable, server-paginated list of every expense across the
 * product (rows link to the read-only detail), followed by a paginated list of every
 * payup (settlement). Both are scoped to the global creation-date range, and each
 * shows an all-time total plus the count added in the selected range.
 */
const ExpensesPage: React.FC = () => {
	const navigate = useNavigate()
	const { from, to } = useAdminFilter()
	const [searchInput, setSearchInput] = useState('')
	const [debouncedQ, setDebouncedQ] = useState('')
	const [page, setPage] = useState(1)
	const [payupPage, setPayupPage] = useState(1)

	const { data, isLoading, isError } = useAdminExpenses({ page, pageSize: PAGE_SIZE, q: debouncedQ })
	const {
		data: payups,
		isLoading: payupsLoading,
		isError: payupsError,
	} = useAdminPayups({ page: payupPage, pageSize: PAGE_SIZE })

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedQ(searchInput.trim()), 300)
		return () => clearTimeout(timer)
	}, [searchInput])

	// Reset the expenses list to page 1 whenever the search term or date range changes.
	useEffect(() => {
		setPage(1)
	}, [debouncedQ, from, to])

	// The payups list isn't searchable, so it only restarts when the date range changes.
	useEffect(() => {
		setPayupPage(1)
	}, [from, to])

	return (
		<>
			<AdminHeading>Expenses</AdminHeading>

			{!isLoading && !isError && data && (
				<ListStats noun="expenses" total={data.totalAllTime} newInRange={data.total} />
			)}
			{!payupsLoading && !payupsError && payups && (
				<ListStats noun="payups" total={payups.totalAllTime} newInRange={payups.total} />
			)}

			<Toolbar>
				<SearchInput
					type="search"
					aria-label="Search expenses by name or trip"
					placeholder="Search by expense or trip name…"
					value={searchInput}
					onChange={e => setSearchInput(e.target.value)}
				/>
				{!isLoading && !isError && data && (
					<ResultCount>
						{data.total.toLocaleString()} {data.total === 1 ? 'expense' : 'expenses'}
					</ResultCount>
				)}
			</Toolbar>

			{isLoading ? (
				<PageLoader label="Loading expenses…" />
			) : isError || !data ? (
				<ErrorState>Could not load expenses. Please try again.</ErrorState>
			) : (
				<>
					<TableWrap>
						<Table>
							<thead>
								<tr>
									<Th>Expense</Th>
									<Th>Trip</Th>
									<Th>Amount</Th>
									<Th>Paid by</Th>
									<Th>People</Th>
									<Th>Created</Th>
								</tr>
							</thead>
							<tbody>
								{data.expenses.length === 0 ? (
									<tr>
										<Td colSpan={6}>
											<ListEmptyState>No expenses match this filter.</ListEmptyState>
										</Td>
									</tr>
								) : (
									data.expenses.map(expense => (
										<Row
											key={expense.id}
											$deleted={expense.isDeleted}
											onClick={() => navigate(`/admin/expenses/${expense.id}`)}
										>
											<Td>
												{expense.name}
												{expense.isDeleted && <DeletedTag>Deleted</DeletedTag>}
											</Td>
											<Td>{expense.tripName}</Td>
											<Td>{formatAmount(expense.amount, expense.currencyCode)}</Td>
											<Td>{expense.paidByName}</Td>
											<Td>{expense.participantCount.toLocaleString()}</Td>
											<Td>{formatDate(expense.createdAt)}</Td>
										</Row>
									))
								)}
							</tbody>
						</Table>
					</TableWrap>

					<Pagination>
						<PageStatus>
							Page {data.page} of {data.totalPages}
						</PageStatus>
						<PageButton disabled={data.page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
							← Prev
						</PageButton>
						<PageButton disabled={data.page >= data.totalPages} onClick={() => setPage(p => p + 1)}>
							Next →
						</PageButton>
					</Pagination>
				</>
			)}

			<SectionHeading>Payups</SectionHeading>

			{payupsLoading ? (
				<PageLoader label="Loading payups…" />
			) : payupsError || !payups ? (
				<ErrorState>Could not load payups. Please try again.</ErrorState>
			) : (
				<>
					<TableWrap>
						<Table>
							<thead>
								<tr>
									<Th>Trip</Th>
									<Th>From → To</Th>
									<Th>Amount</Th>
									<Th>Date</Th>
									<Th>Created</Th>
								</tr>
							</thead>
							<tbody>
								{payups.payups.length === 0 ? (
									<tr>
										<Td colSpan={5}>
											<ListEmptyState>No payups match this filter.</ListEmptyState>
										</Td>
									</tr>
								) : (
									payups.payups.map(payup => (
										<StaticRow key={payup.id} $deleted={payup.isDeleted}>
											<Td>{payup.tripName}</Td>
											<Td>
												{payup.fromName} → {payup.toName}
												{payup.isDeleted && <DeletedTag>Deleted</DeletedTag>}
											</Td>
											<Td>{formatAmount(payup.amount, payup.currencyCode)}</Td>
											<Td>{formatDate(payup.payUpDate)}</Td>
											<Td>{formatDate(payup.createdAt)}</Td>
										</StaticRow>
									))
								)}
							</tbody>
						</Table>
					</TableWrap>

					<Pagination>
						<PageStatus>
							Page {payups.page} of {payups.totalPages}
						</PageStatus>
						<PageButton
							disabled={payups.page <= 1}
							onClick={() => setPayupPage(p => Math.max(1, p - 1))}
						>
							← Prev
						</PageButton>
						<PageButton
							disabled={payups.page >= payups.totalPages}
							onClick={() => setPayupPage(p => p + 1)}
						>
							Next →
						</PageButton>
					</Pagination>
				</>
			)}
		</>
	)
}

export default ExpensesPage
